// ÜGYVEZETŐ-TÖRTÉNET szerkesztő a számlázási-profil sidebarban: ismételhető sorok (név + tól + ig), a
// `PricingEditor` sáv-szerkesztőjének VIZUÁLIS MINTÁJÁRA (keretes-lekerekített sor, rács, szellem-gombos
// törlés, körvonalas „hozzáadás") — hogy a két ismétlődő blokk ne nézzen ki kétféleképpen.
//
// MIÉRT KÜLÖN FÁJL és nem a sheetben: a sheet már így is hosszú, és ennek a blokknak SAJÁT viselkedése van
// (automatikus lezárás + átfedés-jelzés), amit külön akarunk tesztelni.
//
// KÉT SZABÁLY, ami itt él (user-döntés — a ROADMAP A-152 „Ügyvezető-történet" tétele):
//   1. AUTOMATIKUS LEZÁRÁS: ha egy bejegyzésnek kezdőnapot adsz, a még nyitott KORÁBBI bejegyzés
//      végdátuma az azt megelőző napra áll. Az űrlapon történik, LÁTHATÓAN — felülírható (l. `directors.ts`).
//   2. ÁTFEDÉS: nem tiltjuk, csak JELEZZÜK. A hiányos valóságot (dátum nélküli örökölt nevek) rögzíteni
//      kell tudni; az ellentmondást viszont nem hallgatjuk el.
import { ExclamationTriangleIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DateField, TextField } from '@/components/shared/form-fields';
import { Button } from '@/components/ui/button';
import { applyAutoClose, overlappingDirectorIndexes } from '@/features/billing-profiles/directors';
import {
  EMPTY_DIRECTOR,
  type BillingProfileFormValues,
  type DirectorFormValue,
} from '@/features/billing-profiles/form';

/** Az RHF `watch`-callbackje részleges (`DeepPartial`) értéket ad — innen kell biztos alakra hozni. */
function toRows(raw: unknown): DirectorFormValue[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((r) => {
    const o = (r ?? {}) as Partial<DirectorFormValue>;
    return { name: o.name ?? '', startsOn: o.startsOn ?? '', endsOn: o.endsOn ?? '' };
  });
}

export interface ManagingDirectorsFieldProps {
  form: UseFormReturn<BillingProfileFormValues>;
}

export function ManagingDirectorsField({ form }: ManagingDirectorsFieldProps): JSX.Element {
  const { t } = useTranslation();
  const { control, watch, setValue } = form;
  const array = useFieldArray({ control, name: 'managingDirectors' });
  const [autoClosed, setAutoClosed] = useState(false);

  const rows = toRows(watch('managingDirectors'));
  // A figyelmeztetés a TELJES listából dől el, ezért a sorok tartalmára memoizálunk (a `rows` minden
  // renderben új tömb-referencia — ezzel csak akkor számolunk újra, ha tényleg változott valami).
  const rowsKey = JSON.stringify(rows);
  const overlapping = useMemo(
    () => overlappingDirectorIndexes(toRows(JSON.parse(rowsKey))),
    [rowsKey],
  );

  // AUTOMATIKUS LEZÁRÁS. Az RHF `watch`-FELIRATKOZÁSA (nem a render-beli érték) azért kell, mert így
  // megkapjuk, MELYIK mező változott — egy render-szintű összehasonlítás vagy végtelen körbe futna, vagy
  // nem tudná megkülönböztetni a „sort adtam hozzá" esetet a „dátumot írtam be"-től.
  useEffect(() => {
    const sub = watch((values, { name }) => {
      const match = /^managingDirectors\.(\d+)\.startsOn$/.exec(name ?? '');
      if (!match) return;
      const index = Number(match[1]);
      const current = toRows(values.managingDirectors);
      const next = applyAutoClose(current, index);
      let changed = false;
      next.forEach((row, i) => {
        if (i === index || row.endsOn === current[i]?.endsOn) return;
        setValue(`managingDirectors.${i}.endsOn`, row.endsOn, { shouldDirty: true });
        changed = true;
      });
      if (changed) setAutoClosed(true);
    });
    return () => sub.unsubscribe();
  }, [watch, setValue]);

  return (
    <div className="border-border space-y-4 rounded-lg border p-4">
      <div className="space-y-1">
        <p className="text-sm font-semibold">{t('billingProfiles.directors.label')}</p>
        <p className="text-subtle text-xs">{t('billingProfiles.directors.sectionHint')}</p>
      </div>

      {array.fields.length === 0 && (
        <p className="text-muted-foreground text-sm">{t('billingProfiles.directors.empty')}</p>
      )}

      {array.fields.length > 0 && (
        <div className="space-y-3">
          {array.fields.map((field, index) => (
            <div key={field.id} className="space-y-2">
              <div className="border-border grid grid-cols-1 gap-3 rounded-lg border p-3 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1.3fr)]">
                <TextField<BillingProfileFormValues>
                  name={`managingDirectors.${index}.name`}
                  label={t('billingProfiles.directors.name')}
                  autoComplete="off"
                />
                <DateField<BillingProfileFormValues>
                  name={`managingDirectors.${index}.startsOn`}
                  label={t('billingProfiles.directors.startsOn')}
                />
                {/* A törlés a ZÁRÓ dátum mellett áll (ezért kap a harmadik oszlop több helyet: a
                    dátum + a gomb együtt ül benne — szűkebben a dátum-felirat elvágódott, mérve) (a `PricingEditor` sáv-sorának mintája): így mobilon
                    sem lóg ki külön sorba, és a sor jobb alsó sarkában mindig ugyanott van. */}
                <div className="flex items-end gap-2">
                  <div className="min-w-0 flex-1">
                    <DateField<BillingProfileFormValues>
                      name={`managingDirectors.${index}.endsOn`}
                      label={t('billingProfiles.directors.endsOn')}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('billingProfiles.directors.remove')}
                    onClick={() => array.remove(index)}
                  >
                    <TrashIcon className="text-destructive h-4 w-4" />
                  </Button>
                </div>
              </div>
              {overlapping.has(index) && (
                <p className="text-warning-500 flex items-start gap-1.5 text-xs">
                  <ExclamationTriangleIcon className="mt-px size-4 shrink-0" />
                  {t('billingProfiles.directors.overlapWarning')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {autoClosed && (
        <p className="text-subtle text-xs">{t('billingProfiles.directors.autoClosedHint')}</p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => array.append({ ...EMPTY_DIRECTOR })}
      >
        <PlusIcon className="h-4 w-4" />
        {t('billingProfiles.directors.add')}
      </Button>
    </div>
  );
}
